import React, { useMemo, useState } from 'react'
import { Alert, Flex, Spin, Table, Typography } from 'antd'
import CategoryDetailsTable from './CategoryDetailsTable'

const { Text } = Typography

const AcceptanceAnalyticsByCategoryTable = ({
    analytics,
    isFetching,
    error,
    description,
}) => {
    const [expandedRowKeys, setExpandedRowKeys] = useState([])
    const [expandedRubricRowKeys, setExpandedRubricRowKeys] = useState([])

    const { confidenceLevels, rows } = useMemo(() => {
        if (!analytics) return { confidenceLevels: [], rows: [] }

        const confidenceLevels = analytics.allQuestions?.confidenceData?.map(c => c.confidenceLevel) || []

        const questionRows = (analytics.questions || []).map(question => ({
            key: `question-${question.questionId}`,
            questionName: question.questionName,
            confidenceData: question.confidenceData,
            questionRubrics: question.questionRubrics || [],
            isExpandable: (question.questionRubrics || []).length > 0,
        }))

        const allQuestionRow = {
            key: 'ALL',
            questionName: analytics.allQuestions?.questionName || 'ALL',
            confidenceData: analytics.allQuestions?.confidenceData || [],
            isExpandable: false,
        }

        return { confidenceLevels, rows: [...questionRows, allQuestionRow] }
    }, [analytics])

    const columns = useMemo(() => {
        if (!confidenceLevels.length) return []

        return [
            {
                title: 'Question',
                dataIndex: 'questionName',
                key: 'questionName',
                fixed: 'left',
                width: 250,
                render: text => <Text strong>{text}</Text>,
            },
            ...confidenceLevels.map(level => ({
                title: level,
                key: level,
                align: 'center',
                width: 150,
                render: (_, record) => {
                    let percentage
                    if (record.confidenceData) {
                        const confidenceData = record.confidenceData.find(c => c.confidenceLevel === level)
                        percentage = confidenceData?.percentage
                    } else {
                        percentage = record[level]
                    }
                    return percentage === 'N/A' ? 'N/A' : `${percentage}%`
                },
            })),
        ]
    }, [confidenceLevels])

    const expandedRowRender = record => {
        if (!record.questionRubrics?.length) return null

        const rubricData = record.questionRubrics.map(qr => {
            const row = {
                key: `rubric-${qr.questionRubricId}`,
                questionName: qr.rubricName,
                isExpandable: (qr.categories?.length > 0),
                categories: qr.categories || [],
            }

            qr.confidenceData?.forEach(confidence => {
                row[confidence.confidenceLevel] = confidence.percentage
            })

            return row
        })

        const rubricExpandedRowRender = rubricRecord => (
            <CategoryDetailsTable
                categories={rubricRecord.categories}
                confidenceLevels={confidenceLevels}
            />
        )

        return (
            <Table
                columns={columns}
                dataSource={rubricData}
                pagination={false}
                size='small'
                bordered
                showHeader={false}
                rowKey='key'
                expandable={{
                    expandedRowRender: rubricExpandedRowRender,
                    expandedRowKeys: expandedRubricRowKeys,
                    onExpand: (expanded, rubricRecord) => {
                        setExpandedRubricRowKeys(prev =>
                            expanded ? [...prev, rubricRecord.key] : prev.filter(k => k !== rubricRecord.key)
                        )
                    },
                    rowExpandable: rubricRecord => rubricRecord.isExpandable,
                }}
            />
        )
    }

    if (error) return <Alert message={error} type='error' showIcon />

    return (
        <Spin spinning={isFetching} tip='Loading...'>
            <Flex vertical gap={16}>
                <Text type='secondary'>{description}</Text>
                <Table
                    columns={columns}
                    dataSource={rows}
                    pagination={false}
                    scroll={{ x: true }}
                    size='small'
                    bordered
                    rowKey='key'
                    expandable={{
                        expandedRowRender,
                        expandedRowKeys,
                        onExpand: (expanded, record) => {
                            setExpandedRowKeys(prev =>
                                expanded ? [...prev, record.key] : prev.filter(k => k !== record.key)
                            )
                        },
                        rowExpandable: record => record.isExpandable,
                    }}
                    rowClassName={record => (record.key === 'ALL' ? 'ant-table-row-selected' : '')}
                />
            </Flex>
        </Spin>
    )
}

export default AcceptanceAnalyticsByCategoryTable